import type { WorkflowRules, WorkflowStep } from '@/types/workflows';

/**
 * The client-side mirror of the server's draft validation.
 *
 * A MIRROR, not the law. The server's validate endpoint is the ruling and its
 * report is shown verbatim; this function exists so the designer can say "this
 * draft cannot activate, and here is why" while the administrator is still
 * typing, instead of after a round trip. Because it runs on every keystroke it
 * must be pure and cheap, and because it advises rather than rules it reports
 * issue CODES — the page maps them to translated sentences, and anything the
 * server finds that this mirror missed still lands on screen in the server's
 * own words.
 *
 * Each issue carries the step it names (1-based, matching what the card shows)
 * so the designer can pin the message to the card instead of listing riddles.
 */

export interface DraftIssue {
  code:
    | 'NO_STEPS'
    | 'STEP_TITLE_MISSING'
    | 'NO_APPROVERS'
    | 'ROLE_SINGLE_REF'
    | 'QUORUM_RANGE'
    | 'TIMEOUT_INVALID'
    | 'ESCALATION_WITHOUT_TIMEOUT'
    | 'SELF_APPROVAL_CONTRADICTION'
    | 'DISTINCT_UNSATISFIABLE'
    | 'CONDITION_FIELD_MISSING'
    | 'CONDITION_VALUE_MISSING';
  /** 1-based step number the issue points at; absent for workflow-wide issues. */
  stepNo?: number;
}

export function validateDraft(steps: readonly WorkflowStep[], rules: WorkflowRules): DraftIssue[] {
  const issues: DraftIssue[] = [];

  if (steps.length === 0) {
    issues.push({ code: 'NO_STEPS' });
  }

  steps.forEach((step, index) => {
    const stepNo = index + 1;

    if (step.title.trim().length === 0) {
      issues.push({ code: 'STEP_TITLE_MISSING', stepNo });
    }

    if (step.approverRefs.length === 0) {
      issues.push({ code: 'NO_APPROVERS', stepNo });
    } else {
      // ROLE means "this one role decides"; several refs under it are ambiguous
      // — the author wanted ANY_OF_ROLES and the UI nudges them there.
      if (step.approverType === 'ROLE' && step.approverRefs.length > 1) {
        issues.push({ code: 'ROLE_SINGLE_REF', stepNo });
      }
      if (step.quorum < 1 || step.quorum > step.approverRefs.length) {
        issues.push({ code: 'QUORUM_RANGE', stepNo });
      }
    }

    if (step.timeoutHours !== null && (!Number.isFinite(step.timeoutHours) || step.timeoutHours <= 0)) {
      issues.push({ code: 'TIMEOUT_INVALID', stepNo });
    }

    // An escalation target with no timeout is a threat that never fires.
    if (step.escalateTo !== null && step.timeoutHours === null) {
      issues.push({ code: 'ESCALATION_WITHOUT_TIMEOUT', stepNo });
    }

    // A step cannot both allow self-approval and live under a rule that forbids
    // the initiator from approving: for the maker those are the same person,
    // and the toggle would silently lose to the rule at runtime. Said here so
    // the contradiction is resolved by the author, not discovered by the maker.
    if (step.allowSelfApproval && rules.initiatorCannotApprove) {
      issues.push({ code: 'SELF_APPROVAL_CONTRADICTION', stepNo });
    }
  });

  /*
   * Distinct-approvers feasibility. The rule says no one person may decide two
   * steps. The mirror cannot know people — refs are roles and positions — but
   * one contradiction is visible from the definition alone: two steps that each
   * require ALL of their listed approvers (quorum == refs) while sharing a ref
   * force that shared approver to decide both, which the rule forbids. The
   * server may find subtler infeasibilities; this catches the one that is
   * provable without a directory.
   */
  if (rules.distinctApproversAcrossSteps) {
    for (let a = 0; a < steps.length; a += 1) {
      for (let b = a + 1; b < steps.length; b += 1) {
        const first = steps[a];
        const second = steps[b];
        const firstNeedsAll = first.approverRefs.length > 0 && first.quorum === first.approverRefs.length;
        const secondNeedsAll =
          second.approverRefs.length > 0 && second.quorum === second.approverRefs.length;
        if (
          firstNeedsAll &&
          secondNeedsAll &&
          first.approverRefs.some((ref) => second.approverRefs.includes(ref))
        ) {
          issues.push({ code: 'DISTINCT_UNSATISFIABLE', stepNo: b + 1 });
        }
      }
    }
  }

  if (rules.condition !== null) {
    if (rules.condition.field.trim().length === 0) {
      issues.push({ code: 'CONDITION_FIELD_MISSING' });
    }
    const value = rules.condition.value;
    const empty =
      (typeof value === 'string' && value.trim().length === 0) ||
      (Array.isArray(value) && value.length === 0);
    if (empty) {
      issues.push({ code: 'CONDITION_VALUE_MISSING' });
    }
  }

  return issues;
}
